import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'

import Table from 'components/table/SimpleTable'
import Window from 'components/dialog/Window'
import FormDialog from 'components/form/Dialog'

import routerActions from 'store/modules/router'
import adminActions from 'store/modules/admin'
import adminSelectors from 'store/selectors/admin'
import authSelectors from 'store/selectors/auth'
import bookingformSelectors from 'store/selectors/bookingform'
import bookingformActions from 'store/modules/bookingform'
import projectSelectors from 'store/selectors/project'
import projectActions from 'store/modules/project'

import {
  fullUrl,
} from 'utils/url'

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(4),
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'row',
  },
  title: {
    flexGrow: 1,
  },
  addButton: {
    flexGrow: 0,
  },
  actionButton: {
    margin: theme.spacing(1),
  },
  description: {
    color: '#999'
  }
}))

const FORM_SCHEMA = [{
  id: 'email',
  title: 'Email',
  helperText: 'Enter the user email address',
  validate: {
    type: 'string',
    methods: [
      ['required', 'The email is required'],
      ['email', 'Must be a valid email address'],
    ]
  }
}, {
  id: 'role',
  title: 'Role',
  helperText: 'What role does the user have',
  component: 'select',
  default: 'schedule',
  options: [{
    title: 'Schedule',
    value: 'schedule',
  }, {
    title: 'Admin',
    value: 'admin',
  }],
}, {
  id: 'password',
  title: 'Password',
  helperText: 'Enter the user password',
  inputProps: {
    type: 'password',
  }
}, {
  id: 'confirm_password',
  title: 'Confirm Password',
  helperText: 'Confirm the user password',
  inputProps: {
    type: 'password',
  }
}]

const FIELDS = [{
  title: 'Email',
  name: 'email',
}, {
  title: 'Role',
  name: 'role',
}]

const Users = ({

}) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const [ editItem, setEditItem ] = useState(null)
  const [ deletingItem, setDeletingItem ] = useState(null)

  const projectId = useSelector(projectSelectors.currentProjectId)
  const data = useSelector(projectSelectors.users)

  const handlers = useMemo(() => {
    return {
      validate: (values) => {
        const errors = {}
        if(editItem.id == 'new') {
          if(!values.password) errors.password = `please enter a password`
          if(!values.confirm_password) errors.confirm_password = `please enter a confirm password`
        }
        if(values.password && values.password != values.confirm_password) {
          errors.password = errors.confirm_password = `password and confirm password must match`
        }
        return errors
      },
    }
  }, [
    editItem,
  ])
  
  const onAdd = useCallback(() => {
    setEditItem({
      id: 'new',
      data: {
        email: '',
        role: 'schedule',
        password: '',
        confirm_password: '',
      }
    })
  }, [])

  const onEdit = useCallback((item) => {
    setEditItem({
      id: item.id,
      data: item.data,
    })
  }, [])

  const onSaveForm = useCallback(async (payload) => {
    if(editItem.id == 'new') {
      const result = await dispatch(projectActions.createUser(payload))
      if(result) setEditItem(null)
    }
    else {
      const result = await dispatch(projectActions.updateUser(editItem.id, payload))
      if(result) setEditItem(null)
    }
  }, [
    editItem,
  ])

  const onCancelDelete = useCallback(() => {
    setDeletingItem(null)
  })

  const onConfirmDelete = useCallback(async () => {
    const result = await dispatch(projectActions.deleteUser(deletingItem.id))
    if(result) setDeletingItem(null)
  }, [
    deletingItem,
  ])

  const tableData = useMemo(() => {
    return data.map(user => {
      const item = {
        id: user.id,
        data: user,
        email: user.email,
        role: user.role,
      }
      return item
    })
  }, [
    data,
  ])

  const getActions = useCallback((item) => {
    return (
      <>
        <Button
          className={ classes.actionButton }
          size="small"
          variant="outlined"
          onClick={ () => setDeletingItem(item) }
        >
          Delete
        </Button>
        <Button
          className={ classes.actionButton }
          size="small"
          variant="outlined"
          onClick={ (e) => {
            onEdit(item)
            return false
          }}
        >
          Edit
        </Button>
      </>
    )
  })

  useEffect(() => {
    dispatch(projectActions.loadUsers())
  }, [
    projectId,
  ])

  return (
    <div className={ classes.root }>
      <div className={ classes.toolbar }>
        <div className={ classes.title }>
          <Typography gutterBottom variant="h6">
            Users
          </Typography>
        </div>
        <div className={ classes.addButton }>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={ onAdd }
          >
            Add User
          </Button>
        </div>
      </div>
      <div className={ classes.content }>
        <Table
          data={ tableData }
          fields={ FIELDS }
          getActions={ getActions }
        />
      </div>
      {
        deletingItem && (
          <Window
            open
            withCancel
            size="sm"
            title="Confirm delete"
            submitTitle="Delete"
            onCancel={ onCancelDelete }
            onSubmit={ onConfirmDelete }
          >
            <Typography>
              Are you absolutely sure you want to delete the { deletingItem.email } user?
            </Typography>
          </Window>
        )
      }
      {
        editItem && (
          <FormDialog
            withCancel
            size="sm"
            schema={ FORM_SCHEMA }
            handlers={ handlers }
            initialValues={ editItem.data }
            onCancel={ () => setEditItem(null) }
            onSubmit={ onSaveForm }
          />
        )
      }
    </div>
  )
}

export default Users